import { Link } from 'react-router-dom';
import { LEGACY_COMMANDS } from './CommandLegacyPage';

interface CommandCard {
  path: string;
  label: string;
  status: string;
  note: string;
}

/**
 * The routed overview of Command.
 *
 * It is a map, not a dashboard: every destination is listed with what it
 * actually is. A destination that is not built yet says so, and the blueprint
 * card says "no source yet" — nothing on this page claims live data it does
 * not have.
 */
const COMMAND_CARDS: CommandCard[] = [
  {
    path: '/command/allowance',
    label: 'Allowance',
    status: 'Pending — not built yet',
    note: 'No reader yet. The scaffold marks the destination as reachable and honest.',
  },
  {
    path: '/command/models',
    label: 'Models',
    status: 'Pending — not built yet',
    note: 'No reader yet. The scaffold marks the destination as reachable and honest.',
  },
  {
    path: '/command/pipeline',
    label: 'Pipeline',
    status: 'Pending — not built yet',
    note: 'No reader yet. The scaffold marks the destination as reachable and honest.',
  },
  {
    path: '/command/decisions',
    label: 'Decisions',
    status: 'Pending — not built yet',
    note: 'No reader yet. The scaffold marks the destination as reachable and honest.',
  },
  {
    path: '/command/blueprints',
    label: 'Blueprints',
    status: 'Preserved blueprint — no source yet',
    note: 'Preserved command surfaces whose source is not wired. Nothing there is live telemetry.',
  },
];

export function CommandOverviewPage() {
  const preservedPanels = LEGACY_COMMANDS.filter((entry) => entry.panel);

  return (
    <section aria-label="Command overview">
      <div className="mb-3 flex items-baseline justify-between gap-3 border-b border-gray-800 pb-2">
        <h2 className="text-sm font-mono uppercase tracking-[0.2em] text-white">Command</h2>
        <span className="shrink-0 text-[9px] uppercase tracking-wider text-gray-400">Overview</span>
      </div>

      <p className="text-xs font-mono text-gray-400">
        The operator cockpit. Every destination below is a route: it opens by URL, it survives a
        reload, and it is in the browser history. Where a page has no source yet, it says so
        instead of showing a figure.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {COMMAND_CARDS.map((card) => (
          <Link
            key={card.path}
            to={card.path}
            className="rounded-lg border border-gray-800 bg-gray-900/20 p-3 transition-colors hover:border-gray-600"
          >
            <div className="flex items-baseline justify-between gap-2">
              <h3 className="text-xs font-mono uppercase tracking-wider text-gray-300">{card.label}</h3>
              <span className="shrink-0 text-[9px] uppercase tracking-wider text-yellow-500/70">
                {card.status}
              </span>
            </div>
            <p className="mt-2 text-[10px] font-mono text-gray-400">{card.note}</p>
          </Link>
        ))}
      </div>

      <div className="mt-6 border-b border-gray-800 pb-2">
        <h3 className="text-xs font-mono uppercase tracking-wider text-gray-400">Preserved operator panels</h3>
      </div>
      <p className="mt-2 text-[10px] font-mono text-gray-400">
        Every tab of the old Command bar, still reachable on its own route.
      </p>
      <ul className="mt-2 grid gap-1 sm:grid-cols-2 xl:grid-cols-3">
        {preservedPanels.map((entry) => (
          <li key={entry.mode}>
            <Link
              to={entry.canonical}
              className="block rounded px-2 py-1 text-[11px] font-mono uppercase tracking-wider text-gray-400 transition-colors hover:text-gray-200"
            >
              {entry.label}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
